import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import Explainer, { type ExplainerSection } from '../../../components/Explainer';
import {
  type ApiErgebnis,
  type Erwartet,
  ladeHoch,
  ladeProduktMedien,
  ladeVersionen,
  PRODUKT_SLOT_LABEL,
  PRODUKT_VARIANTE_LABEL,
  PRODUKT_VARIANTEN,
  type ProduktMedienAntwort,
  type ProduktMedienVersion,
  type ProduktSlot,
  type ProduktSlotStand,
  REFERENZ_SLOTS,
  type StatusWert,
  type StreckenStatus,
  stelleWiederHer,
} from './produktMedien/api';
import type { PdfLeseErgebnis } from './produktMedien/pdfLesen';
import { ConfirmDialog, formatBerlin, Loading } from './ui';

/**
 * Reiter „Produkt-Medien" der Bestellstrecke (Spec .claude/plans/2026-10-08-produkt-medien.md § 13):
 * je PL-X-Produkt fuenf Faecher (Ausgabe, Quickstart, Bonus, Musterdepot, Autorenfoto). Ein PDF wird
 * im Browser gelesen (produktMedien/pdfLesen.ts, nur per import()), hochgeladen werden Seite 1 als
 * Bild + der Text. Ein Bild-Upload ersetzt nur das Cover.
 *
 * Konflikte: beim Speichern gehen die Zeiger des ANGEZEIGTEN Stands mit (erwarteteCoverId /
 * erwarteteTextId); hat jemand dazwischen gespeichert, antwortet der Server 409 → Hinweis + neu laden.
 * Neue Medien gehen nie automatisch live — „Betroffene Strecken" zeigt, wo neu publiziert werden muss.
 */

const KURZANLEITUNG: ExplainerSection[] = [
  {
    label: 'Fächer',
    body:
      'Jedes PL-X-Produkt hat fünf Fächer: Ausgabe, Quickstart-Guide, Spezialreport / Bonus, ' +
      'Musterdepot und Autorenfoto. Ein Fach hält immer genau eine aktuelle Fassung — sie gilt für ' +
      'alle Webinare dieses Produkts, nicht nur für dieses.',
  },
  {
    label: 'PDF oder fertiges Bild',
    body:
      '„PDF hochladen …“: Seite 1 wird automatisch zum Cover — als „Einzelexemplar“ (ein Heft, ' +
      'leicht gedreht mit Schatten; bei einem fertigen Bild das Bild selbst), „flach“ (Seite 1 ' +
      'ohne Effekt) und bei ' +
      'der Ausgabe zusätzlich als „Fächer“ aus drei Exemplaren; der Text wird ausgelesen. ' +
      '„Bild hochladen …“: ein fertiges Mockup vom Grafiker (PNG, JPEG oder WebP bis 15 MB) — es ' +
      'ersetzt nur das Cover, der Text aus dem PDF bleibt. Soll das Mockup beim nächsten PDF bleiben, ' +
      'den Schalter „Cover aus dem PDF übernehmen“ ausschalten.',
  },
  {
    label: 'Bild auf einer Seite binden',
    body:
      'Reiter „Bearbeiten“ → Bild anklicken → „Aus Produkt-Medien …“ → Fach und Variante wählen → ' +
      'Speichern. Das Bild zeigt nach dem nächsten Veröffentlichen immer die aktuelle Fassung dieses ' +
      'Fachs. „Bindung lösen“ lässt das Bild stehen, aber ohne Bezug zum Fach; „Ersetzen …“ löst ' +
      'die Bindung ebenfalls.',
  },
  {
    label: 'Wichtig: neue Ausgabe erschienen',
    highlight: true,
    body:
      'Neue Ausgabe als PDF hochladen. Unter „Betroffene Strecken“ steht dann bei jeder veröffentlichten ' +
      'Strecke dieses Produkts — auch bei denen anderer Webinare — „neues Cover — bitte prüfen & ' +
      'veröffentlichen“. Die Live-Seiten ändern sich erst, wenn die jeweilige Strecke über die ' +
      'Schritte „Prüfen“ und „Veröffentlichen“ neu veröffentlicht wird — nie automatisch.',
  },
  {
    label: 'KI-Prüfung',
    body:
      'Der Text von Ausgabe und Quickstart-Guide ist Faktenquelle der KI-Prüfung: Zahlen, Fristen, ' +
      'Haltedauer und Leistungsumfang auf der Bestellseite müssen dazu passen. Was nur in den ' +
      'Unterlagen steht, ist kein Befund. Ein Bild-PDF ohne Text kann die KI nicht lesen — dann ' +
      'prüft sie ohne diese Unterlage.',
  },
];

const STATUS_CHIP: Record<StatusWert, { label: string; bg: string; fg: string }> = {
  aktuell: { label: 'aktuell', bg: '#2e7d32', fg: '#ffffff' },
  veraltet: { label: 'neues Cover — bitte prüfen & veröffentlichen', bg: '#ed6c02', fg: '#ffffff' },
  neu_waehlen: {
    label: 'Variante fehlt — Bindung im Editor neu wählen',
    bg: '#d32f2f',
    fg: '#ffffff',
  },
  nur_entwurf: { label: 'Bindung noch nicht veröffentlicht', bg: '#9e9e9e', fg: '#ffffff' },
  nicht_lesbar: {
    label:
      'veröffentlichte Datei fehlt oder ist nicht lesbar — Strecke neu veröffentlichen; hilft das nicht, Admin fragen',
    bg: '#d32f2f',
    fg: '#ffffff',
  },
  ungebunden: { label: 'keine Produktbilder gebunden', bg: '#eeeeee', fg: '#424242' },
};

const BILD_TYPEN = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const BILD_MAX_BYTES = 15 * 1024 * 1024;

const zahl = (n: number) => n.toLocaleString('de-DE');

function quelleText(v: ProduktMedienVersion): string {
  const art = v.sourceKind === 'pdf' ? 'PDF' : 'Bild';
  const datei = v.fileName ? ` „${v.fileName}“` : '';
  const wer = v.createdBy ? ` von ${v.createdBy}` : '';
  return `${art}${datei} · ${formatBerlin(v.createdAt)}${wer}`;
}

/** Vollstaendigkeit des Referenztextes in Worten (Spec § 13, Codex P2-8). */
export function textVollstaendigkeit(
  stand: ProduktSlotStand,
  referenzMax: number,
): { text: string; warnung: boolean } | null {
  const istReferenz = REFERENZ_SLOTS.includes(stand.slot);
  const t = stand.text;
  if (!t || !t.textChars) {
    if (stand.cover?.sourceKind === 'pdf' && !stand.cover.textChars)
      return {
        text: 'kein Text gefunden — Bild-PDF, Prüfung ohne diese Unterlage',
        warnung: istReferenz,
      };
    return istReferenz
      ? { text: 'kein Referenztext — die KI-Prüfung läuft ohne diese Unterlage', warnung: true }
      : null;
  }
  const n = t.textChars;
  const teile: string[] = [];
  let warnung = false;
  if (t.textTruncated) {
    warnung = true;
    if (t.textPagesRead != null && t.pageCount != null && t.textPagesRead < t.pageCount)
      teile.push(`nur ${zahl(t.textPagesRead)} von ${zahl(t.pageCount)} Seiten gelesen`);
    else teile.push('beim Lesen im Browser gekappt');
  }
  if (!istReferenz) {
    teile.unshift(`${zahl(n)} Zeichen (nicht Teil der KI-Prüfung)`);
  } else if (n > referenzMax) {
    warnung = true;
    teile.unshift(`gekürzt: die KI sieht ${zahl(referenzMax)} von ${zahl(n)} Zeichen`);
  } else if (t.textTruncated) {
    teile.unshift(`${zahl(n)} Zeichen in der KI-Prüfung`);
  } else {
    teile.unshift(`${zahl(n)} Zeichen — vollständig in der KI-Prüfung`);
  }
  return { text: teile.join(' · '), warnung };
}

function fehlerText(r: ApiErgebnis<unknown>): string {
  if (r.status === 409)
    return 'Inzwischen hat jemand anderes dieses Fach geändert — der Stand wurde neu geladen. Bitte prüfen und erneut übernehmen.';
  if (r.status === 503)
    return r.error || 'Produkt-Medien sind noch nicht eingerichtet (Migration fehlt).';
  return r.error || 'Speichern fehlgeschlagen';
}

function StatusChip({ status }: { status: StatusWert }) {
  const c = STATUS_CHIP[status] ?? STATUS_CHIP.ungebunden;
  return (
    <Chip
      size="small"
      label={c.label}
      sx={{ backgroundColor: c.bg, color: c.fg, fontWeight: 500 }}
      data-status={status}
    />
  );
}

interface Props {
  webinarId: string;
  /** Ungespeicherter Upload-Entwurf in einem Fach — der Dialog fragt vor Reiterwechsel nach. */
  onDirtyChange?: (dirty: boolean) => void;
}

export default function ProduktMedienPanel({ webinarId, onDirtyChange }: Props) {
  const [antwort, setAntwort] = useState<ProduktMedienAntwort | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [dirtyFaecher, setDirtyFaecher] = useState<Record<string, boolean>>({});

  const lade = useCallback(async (): Promise<boolean> => {
    setLaedt(true);
    const r = await ladeProduktMedien(webinarId);
    setLaedt(false);
    if (!r.success || !r.data) {
      setLadeFehler(r.error || 'Produkt-Medien konnten nicht geladen werden');
      return false;
    }
    setLadeFehler(null);
    setAntwort(r.data);
    return true;
  }, [webinarId]);

  useEffect(() => {
    let cancelled = false;
    setAntwort(null);
    (async () => {
      const r = await ladeProduktMedien(webinarId);
      if (cancelled) return;
      setLaedt(false);
      if (!r.success || !r.data) {
        setLadeFehler(r.error || 'Produkt-Medien konnten nicht geladen werden');
        return;
      }
      setLadeFehler(null);
      setAntwort(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [webinarId]);

  const anyDirty = Object.values(dirtyFaecher).some(Boolean);
  useEffect(() => {
    onDirtyChange?.(anyDirty);
  }, [anyDirty, onDirtyChange]);

  const setFachDirty = useCallback((slot: ProduktSlot, d: boolean) => {
    setDirtyFaecher((prev) => (Boolean(prev[slot]) === d ? prev : { ...prev, [slot]: d }));
  }, []);

  const uebernimmFach = useCallback((fach: ProduktSlotStand, strecken: StreckenStatus[]) => {
    setAntwort((prev) =>
      prev
        ? {
            ...prev,
            faecher: prev.faecher.map((f) => (f.slot === fach.slot ? fach : f)),
            strecken,
          }
        : prev,
    );
  }, []);

  if (!antwort && laedt) return <Loading label="Produkt-Medien laden …" />;
  if (!antwort)
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void lade()}>
            Neu laden
          </Button>
        }
      >
        {ladeFehler || 'Produkt-Medien konnten nicht geladen werden'}
      </Alert>
    );

  const kopf = antwort.plxProduct
    ? `Produkt: ${antwort.produktName || antwort.plxProduct} (${antwort.plxProduct}) — gilt für alle Webinare dieses Produkts`
    : 'Kein PL-X-Produkt zugeordnet';

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          {kopf}
        </Typography>
        <Button size="small" onClick={() => void lade()} disabled={laedt}>
          {laedt ? 'Lädt …' : 'Neu laden'}
        </Button>
      </Box>
      {ladeFehler && <Alert severity="error">{ladeFehler}</Alert>}

      <Explainer title="Kurzanleitung Produkt-Medien" sections={KURZANLEITUNG} />

      {!antwort.plxProduct ? (
        <Alert severity="info">
          Diesem Webinar ist kein PL-X-Produkt zugeordnet. Produkt-Medien hängen am Produkt — erst
          in den Webinar-Einstellungen das Produkt setzen, dann hier Ausgabe, Quickstart & Co.
          hochladen.
        </Alert>
      ) : !antwort.bereit ? (
        <Alert severity="warning">
          Produkt-Medien sind noch nicht eingerichtet (die Datenbank-Erweiterung fehlt noch). Bitte
          Till Bescheid geben — bis dahin ist kein Hochladen möglich.
        </Alert>
      ) : (
        <>
          <Alert severity="info">
            <b>Gilt für alle Webinare dieses Produkts.</b> Eine neue Fassung ändert auf keiner Seite
            sofort etwas: Jede veröffentlichte Strecke dieses Produkts — auch die anderer Webinare —
            zeigt danach unten bei „Betroffene Strecken“ „neues Cover — bitte prüfen &amp;
            veröffentlichen“. Neue Medien gehen nie automatisch live: erst wenn die jeweilige Strecke
            neu geprüft und veröffentlicht ist, steht das neue Bild dort im Netz.
          </Alert>
          <Box
            display="grid"
            gap={2}
            sx={{ gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}
          >
            {antwort.faecher.map((f) => (
              <FachKarte
                key={f.slot}
                plxProduct={antwort.plxProduct as string}
                stand={f}
                referenzMax={antwort.referenzMaxZeichen}
                onAktualisiert={uebernimmFach}
                onNeuLaden={lade}
                onDirtyChange={setFachDirty}
              />
            ))}
          </Box>
        </>
      )}

      {antwort.plxProduct && (
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#ffffff' }}>
          <Typography variant="subtitle2" gutterBottom>
            Betroffene Strecken
          </Typography>
          {antwort.strecken.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Keine Bestellstrecke dieses Produkts.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={0.75}>
              {antwort.strecken.map((s) => (
                <Box
                  key={s.webinarId}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                  data-testid={`strecke-${s.webinarId}`}
                >
                  <Typography variant="body2" sx={{ minWidth: 220, flex: 1 }}>
                    {s.titel || s.webinarId}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {s.webinarId}
                      {s.webinarId === webinarId ? ' · diese Strecke' : ''}
                    </Typography>
                  </Typography>
                  <StatusChip status={s.status} />
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------
// Ein Fach
// ---------------------------------------------------------------------------------------------

type Entwurf =
  | { art: 'pdf'; datei: File; ergebnis: PdfLeseErgebnis; vorschau: string | null; cover: boolean }
  | { art: 'bild'; datei: File; vorschau: string | null };

const objektUrl = (b: Blob): string | null =>
  typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(b)
    : null;
const freigeben = (u: string | null) => {
  if (u && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function')
    URL.revokeObjectURL(u);
};

interface FachProps {
  plxProduct: string;
  stand: ProduktSlotStand;
  referenzMax: number;
  onAktualisiert: (fach: ProduktSlotStand, strecken: StreckenStatus[]) => void;
  onNeuLaden: () => Promise<boolean>;
  onDirtyChange: (slot: ProduktSlot, dirty: boolean) => void;
}

function FachKarte({
  plxProduct,
  stand,
  referenzMax,
  onAktualisiert,
  onNeuLaden,
  onDirtyChange,
}: FachProps) {
  const slot = stand.slot;
  const pdfRef = useRef<HTMLInputElement | null>(null);
  const bildRef = useRef<HTMLInputElement | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [liest, setLiest] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [meldung, setMeldung] = useState<{
    art: 'success' | 'error' | 'warning';
    text: string;
  } | null>(null);
  const [verlaufOffen, setVerlaufOffen] = useState(false);
  const [versionen, setVersionen] = useState<ProduktMedienVersion[] | null>(null);
  const [verlaufFehler, setVerlaufFehler] = useState<string | null>(null);
  const [restoreZiel, setRestoreZiel] = useState<ProduktMedienVersion | null>(null);
  const abbruchRef = useRef<AbortController | null>(null);

  useEffect(() => {
    onDirtyChange(slot, entwurf !== null);
  }, [entwurf, slot, onDirtyChange]);

  // Vorschau-URL freigeben, wenn der Entwurf wechselt oder die Karte verschwindet.
  useEffect(() => {
    const u = entwurf?.vorschau ?? null;
    return () => freigeben(u);
  }, [entwurf]);
  useEffect(() => () => abbruchRef.current?.abort(), []);

  const bildIstCover = stand.cover?.sourceKind === 'bild';
  const vollstaendig = textVollstaendigkeit(stand, referenzMax);
  const varianten = PRODUKT_VARIANTEN.filter((v) => stand.cover?.urls?.[v]);

  /** Zeiger des ANGEZEIGTEN Stands — der Server prueft sie (409 bei Zwischenaenderung). */
  const erwartet = (): { cover: Erwartet; text: Erwartet } => ({
    cover: stand.cover ? stand.cover.id : null,
    text: stand.text ? stand.text.id : null,
  });

  const ladeVerlauf = useCallback(async () => {
    setVerlaufFehler(null);
    const r = await ladeVersionen(plxProduct, slot);
    if (r.success && r.data) setVersionen(r.data.versionen);
    else setVerlaufFehler(r.error || 'Verlauf konnte nicht geladen werden');
  }, [plxProduct, slot]);

  const onPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const datei = e.target.files?.[0];
    e.target.value = '';
    if (!datei) return;
    setMeldung(null);
    if (datei.type && datei.type !== 'application/pdf' && !/\.pdf$/i.test(datei.name)) {
      setMeldung({ art: 'error', text: 'Bitte eine PDF-Datei wählen.' });
      return;
    }
    abbruchRef.current?.abort();
    const ac = new AbortController();
    abbruchRef.current = ac;
    setLiest(true);
    try {
      const { lesePdf } = await import('./produktMedien/pdfLesen');
      const ergebnis = await lesePdf(datei, ac.signal);
      if (ac.signal.aborted) return;
      setEntwurf({
        art: 'pdf',
        datei,
        ergebnis,
        vorschau: objektUrl(ergebnis.png),
        // Vorgabe an — aus, wenn das aktuelle Cover ein eigenes Bild (Mockup) ist.
        cover: !bildIstCover,
      });
    } catch (err) {
      if (!ac.signal.aborted)
        setMeldung({ art: 'error', text: (err as Error)?.message || 'PDF nicht lesbar' });
    } finally {
      if (abbruchRef.current === ac) abbruchRef.current = null;
      setLiest(false);
    }
  };

  const onBild = (e: React.ChangeEvent<HTMLInputElement>) => {
    const datei = e.target.files?.[0];
    e.target.value = '';
    if (!datei) return;
    setMeldung(null);
    if (!BILD_TYPEN.includes(datei.type)) {
      setMeldung({ art: 'error', text: 'Nur PNG, JPEG oder WebP werden als Bild angenommen.' });
      return;
    }
    if (datei.size > BILD_MAX_BYTES) {
      setMeldung({ art: 'error', text: 'Das Bild ist größer als 15 MB.' });
      return;
    }
    setEntwurf({ art: 'bild', datei, vorschau: objektUrl(datei) });
  };

  const uebernehmen = async () => {
    if (!entwurf) return;
    setSpeichert(true);
    setMeldung(null);
    const e = erwartet();
    const r =
      entwurf.art === 'pdf'
        ? await ladeHoch({
            plxProduct,
            slot,
            quelle: 'pdf',
            bild: entwurf.cover ? entwurf.ergebnis.png : null,
            bildName: entwurf.ergebnis.png.type === 'image/jpeg' ? 'seite1.jpg' : 'seite1.png',
            coverUebernehmen: entwurf.cover,
            text: entwurf.ergebnis.text,
            dateiName: entwurf.datei.name,
            dateiSha256: entwurf.ergebnis.sha256,
            seiten: entwurf.ergebnis.seiten,
            textSeitenGelesen: entwurf.ergebnis.textSeitenGelesen,
            textGekuerzt: entwurf.ergebnis.gekuerzt,
            erwarteteCoverId: e.cover,
            erwarteteTextId: e.text,
          })
        : await ladeHoch({
            plxProduct,
            slot,
            quelle: 'bild',
            bild: entwurf.datei,
            bildName: entwurf.datei.name,
            coverUebernehmen: true,
            dateiName: entwurf.datei.name,
            erwarteteCoverId: e.cover,
            erwarteteTextId: e.text,
          });
    setSpeichert(false);
    if (r.success && r.data) {
      onAktualisiert(r.data.fach, r.data.strecken);
      setEntwurf(null);
      setMeldung({ art: 'success', text: `${PRODUKT_SLOT_LABEL[slot]} gespeichert.` });
      if (verlaufOffen) void ladeVerlauf();
      return;
    }
    setMeldung({ art: r.status === 409 ? 'warning' : 'error', text: fehlerText(r) });
    if (r.status === 409) {
      await onNeuLaden();
      if (verlaufOffen) void ladeVerlauf();
    }
  };

  const wiederherstellen = async () => {
    const v = restoreZiel;
    if (!v) return;
    setSpeichert(true);
    setMeldung(null);
    const r = await stelleWiederHer(plxProduct, slot, v.id, erwartet());
    setSpeichert(false);
    setRestoreZiel(null);
    if (r.success && r.data) {
      onAktualisiert(r.data.fach, r.data.strecken);
      setMeldung({
        art: 'success',
        text: `Fassung vom ${formatBerlin(v.createdAt)} wiederhergestellt.`,
      });
      void ladeVerlauf();
      return;
    }
    setMeldung({ art: r.status === 409 ? 'warning' : 'error', text: fehlerText(r) });
    if (r.status === 409) {
      await onNeuLaden();
      void ladeVerlauf();
    }
  };

  const pdfOhneText = entwurf?.art === 'pdf' && !entwurf.ergebnis.text.trim();
  const nichtsZuSpeichern = entwurf?.art === 'pdf' && !entwurf.cover && pdfOhneText;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: '#ffffff' }}
      data-testid={`fach-${slot}`}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          {PRODUKT_SLOT_LABEL[slot]}
        </Typography>
        {REFERENZ_SLOTS.includes(slot) && (
          <Tooltip title="Der Text dieses Fachs geht als Faktenquelle in die KI-Prüfung der Bestellstrecke.">
            <Chip size="small" variant="outlined" label="Faktenquelle der KI-Prüfung" />
          </Tooltip>
        )}
      </Box>

      {varianten.length > 0 ? (
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="flex-end">
          {varianten.map((v) => (
            <Box key={v} textAlign="center">
              <Box
                component="img"
                src={stand.cover?.urls?.[v]}
                alt={`${PRODUKT_SLOT_LABEL[slot]} ${PRODUKT_VARIANTE_LABEL[v]}`}
                sx={{
                  display: 'block',
                  maxHeight: 140,
                  maxWidth: 200,
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#fafafa',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {PRODUKT_VARIANTE_LABEL[v]}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px dashed #bdbdbd',
            borderRadius: 1,
            p: 2,
            textAlign: 'center',
            color: '#757575',
          }}
        >
          <Typography variant="body2">Noch kein Cover</Typography>
        </Box>
      )}

      <Typography variant="body2">
        <b>Cover:</b> {stand.cover ? quelleText(stand.cover) : '—'}
      </Typography>
      <Typography variant="body2">
        <b>Text:</b>{' '}
        {stand.text
          ? quelleText(stand.text)
          : stand.cover?.sourceKind === 'pdf'
            ? 'aus dem PDF'
            : '—'}
      </Typography>
      {vollstaendig && (
        <Typography
          variant="body2"
          sx={{ color: vollstaendig.warnung ? '#b45309' : '#2e7d32' }}
          data-testid={`vollstaendig-${slot}`}
        >
          {vollstaendig.text}
        </Typography>
      )}

      <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
        <Button
          size="small"
          variant="outlined"
          disabled={liest || speichert}
          onClick={() => pdfRef.current?.click()}
        >
          PDF hochladen …
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={liest || speichert}
          onClick={() => bildRef.current?.click()}
        >
          Bild hochladen …
        </Button>
        <Button
          size="small"
          onClick={() => {
            const neu = !verlaufOffen;
            setVerlaufOffen(neu);
            if (neu) void ladeVerlauf();
          }}
        >
          {verlaufOffen ? 'Verlauf schließen' : 'Verlauf'}
        </Button>
      </Box>
      <input
        ref={pdfRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        data-testid={`pdf-input-${slot}`}
        onChange={onPdf}
      />
      <input
        ref={bildRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        data-testid={`bild-input-${slot}`}
        onChange={onBild}
      />

      {liest && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            PDF wird im Browser gelesen …
          </Typography>
          <Button size="small" onClick={() => abbruchRef.current?.abort()}>
            Abbrechen
          </Button>
        </Box>
      )}

      {meldung && (
        <Alert severity={meldung.art} onClose={() => setMeldung(null)}>
          {meldung.text}
        </Alert>
      )}

      {entwurf && (
        <Box
          sx={{
            border: '1px solid #90caf9',
            borderRadius: 1,
            p: 1.5,
            backgroundColor: '#f5f9ff',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
          data-testid={`entwurf-${slot}`}
        >
          <Typography variant="body2" fontWeight={600}>
            Vorschau: {entwurf.datei.name}
          </Typography>
          <Box display="flex" gap={1.5} alignItems="flex-start" flexWrap="wrap">
            {entwurf.vorschau && (
              <Box
                component="img"
                src={entwurf.vorschau}
                alt="Vorschau"
                sx={{ maxHeight: 220, maxWidth: 180, border: '1px solid #e0e0e0' }}
              />
            )}
            {entwurf.art === 'pdf' ? (
              <Box display="flex" flexDirection="column" gap={0.5} sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body2">
                  {pdfOhneText
                    ? stand.text
                      ? 'kein Text gefunden (Bild-PDF) — der bisherige Referenztext bleibt in der Prüfung'
                      : 'kein Text gefunden — Bild-PDF, Prüfung ohne diese Unterlage'
                    : `${zahl(entwurf.ergebnis.text.length)} Zeichen Text aus ${zahl(
                        entwurf.ergebnis.textSeitenGelesen,
                      )} von ${zahl(entwurf.ergebnis.seiten)} Seiten`}
                </Typography>
                {entwurf.ergebnis.gekuerzt && (
                  <Typography variant="body2" sx={{ color: '#b45309' }}>
                    {entwurf.ergebnis.textSeitenGelesen < entwurf.ergebnis.seiten
                      ? `nur ${zahl(entwurf.ergebnis.textSeitenGelesen)} von ${zahl(entwurf.ergebnis.seiten)} Seiten gelesen`
                      : 'beim Lesen gekappt (Zeichen- oder Zeitbudget)'}
                  </Typography>
                )}
                {REFERENZ_SLOTS.includes(slot) && entwurf.ergebnis.text.length > referenzMax && (
                  <Typography variant="body2" sx={{ color: '#b45309' }}>
                    gekürzt: die KI sieht {zahl(referenzMax)} von{' '}
                    {zahl(entwurf.ergebnis.text.length)} Zeichen
                  </Typography>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={entwurf.cover}
                      onChange={(ev) =>
                        setEntwurf((prev) =>
                          prev && prev.art === 'pdf' ? { ...prev, cover: ev.target.checked } : prev,
                        )
                      }
                      inputProps={{ 'aria-label': 'Cover aus dem PDF übernehmen' }}
                    />
                  }
                  label="Cover aus dem PDF übernehmen"
                />
                {bildIstCover && (
                  <Typography variant="caption" color="text.secondary">
                    Das aktuelle Cover ist ein eigenes Bild (Mockup) — es bleibt, solange der
                    Schalter aus ist; übernommen wird dann nur der Text.
                  </Typography>
                )}
                {nichtsZuSpeichern && (
                  <Typography variant="caption" sx={{ color: '#d32f2f' }}>
                    Ohne Text und ohne Cover gibt es nichts zu speichern.
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
                Ersetzt nur das Cover; ein vorhandener Text aus dem PDF bleibt.
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              onClick={() => void uebernehmen()}
              disabled={speichert || nichtsZuSpeichern}
            >
              {speichert ? 'Speichert …' : 'Übernehmen'}
            </Button>
            <Button size="small" onClick={() => setEntwurf(null)} disabled={speichert}>
              Verwerfen
            </Button>
          </Box>
        </Box>
      )}

      {verlaufOffen && (
        <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1 }} data-testid={`verlauf-${slot}`}>
          {verlaufFehler && <Alert severity="error">{verlaufFehler}</Alert>}
          {!versionen && !verlaufFehler && <Loading label="Verlauf laden …" />}
          {versionen && versionen.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Noch keine Fassung hochgeladen.
            </Typography>
          )}
          {versionen && versionen.length > 0 && (
            <Box display="flex" flexDirection="column" gap={0.75}>
              {versionen.map((v) => {
                const istCover = stand.cover?.id === v.id;
                const istText = stand.text?.id === v.id;
                const aktuell = (istCover || !v.urls) && (istText || !v.textChars);
                return (
                  <Box key={v.id} display="flex" alignItems="center" gap={1}>
                    {v.urls?.einzel ? (
                      <Box
                        component="img"
                        src={v.urls.einzel}
                        alt=""
                        sx={{ width: 40, height: 52, objectFit: 'contain', flexShrink: 0 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 52,
                          flexShrink: 0,
                          border: '1px dashed #bdbdbd',
                          fontSize: 10,
                          color: '#757575',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        Text
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={quelleText(v)}>
                        {quelleText(v)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.urls ? 'mit Cover' : 'ohne Cover'}
                        {v.textChars ? ` · ${zahl(v.textChars)} Zeichen Text` : ''}
                        {istCover ? ' · aktuelles Cover' : ''}
                        {istText ? ' · aktueller Text' : ''}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      disabled={aktuell || speichert}
                      onClick={() => setRestoreZiel(v)}
                    >
                      Wiederherstellen
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      <ConfirmDialog
        open={restoreZiel !== null}
        title="Fassung wiederherstellen?"
        confirmLabel="Wiederherstellen"
        busy={speichert}
        onConfirm={() => void wiederherstellen()}
        onCancel={() => setRestoreZiel(null)}
      >
        <Typography variant="body2">
          {PRODUKT_SLOT_LABEL[slot]} zeigt danach die Fassung vom{' '}
          {restoreZiel ? formatBerlin(restoreZiel.createdAt) : ''}
          {restoreZiel?.urls && restoreZiel.textChars
            ? ' (Cover und Text)'
            : restoreZiel?.urls
              ? ' (Cover)'
              : ' (Text)'}
          . Live geht dadurch nichts — die Strecken danach neu prüfen und veröffentlichen.
        </Typography>
      </ConfirmDialog>
    </Paper>
  );
}
